import React, {useEffect, useState, useMemo, useCallback} from "react";

import {
  Stack,
  Typography,
  Button,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material"

import EditIcon from "@mui/icons-material/Edit"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

import {useLocation, useNavigate, useParams} from "react-router-dom"
import {useDispatch, useSelector} from "react-redux";
import {clientActions} from "redux/clientSlice";
import {loggedInUserState} from "redux/userSlice";
import {vendorActions, vendorState} from "redux/vendorSlice";

import ProjectInfoUpdateModal from "./ProjectInfoUpdateModal";
import UpdateAllSubsCoverageModal from "./UpdateAllSubsCoverageModal";

import Snackbar from "components/shared/Snackbar";
import StatusIndicatorSelect from "components/shared/StatusIndicatorSelect";
import SideMenuContainer from "components/shared/SideMenuContainer";
import "./project-details.scss";
import {NAVIGATED_FROM, getNavigationPath, useNavPath} from "constants/routes";
import {SYSTEM,AGENCY,getVendorLabel, getProjectLabel} from "constants/organization_types"
import { client_mode } from "constants/client_properties";
import cognito_groups from "constants/cognito_groups";

import {organizationState} from "redux/organizationSlice";
import {awsDateToUS} from "utils/dateUtils";
import {SectionTitle} from "components/shared/Typography";
import BoxShadow from "components/shared/BoxShadow";
import CoverageForm from "components/shared/CoverageForm";
import VendorsTable from "components/Vendors/VendorsTable";
import CoverageUpdateModal from "components/shared/CoverageForm/CoverageUpdateModal";
import CertificateTable from "components/CertificateTable";
import AddSubcontractorsModal from "./AddSubcontractorsModal";
import TourOutline from "components/shared/TourOutline";
import { useEntityTour } from "hooks/useTour";
import { COVERAGES_TOUR, DEFAULT_TOUR_STYLES, TOUR_TITLES, CustomTooltip } from "config/tours";

const projectStatuses = {
  ACTIVE: {label: "Active", optionLabel: "Make Active"},
  INACTIVE: {label: "Inactive", optionLabel: "Make Inactive"},
  ARCHIVED: {label: "Archived", optionLabel: "Archive"},
}


export default function ProjectDetails() {
  const navigate = useNavigate();
  const { clientId, projectId } = useParams();
  const { userOrgType, selectedClient } = useSelector(organizationState)
  const { vendors } = useSelector(vendorState)
  const projects = useSelector(state => state.client.projects[selectedClient?.clientID]) || []
  const { isAdmin, loggedInUser } = useSelector(loggedInUserState)
  const filterByAssignedVendors = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role) ? loggedInUser?.assignedVendors : []
  const hasEditPrivileges = isAdmin //useMemo(() => [SYSTEM, AGENCY].includes(userOrgType), [userOrgType])
  const dispatch = useDispatch()
  const NAV_PATH = useNavPath(userOrgType)
  const project = projects.find(oneProject => oneProject.id === projectId)
  const coverages = JSON.parse(project?.requiredCoverages || "[]")
  const location = useLocation()
  const [certificates, setCertificates] = useState()
  const vendorLabel = getVendorLabel(selectedClient);
  const projectLabel = getProjectLabel(selectedClient);
  const subMenuItems = [
    {key: "coverages", label: "Required Coverages", className: "coverage-menu-coverages"},
    {key: "subcontractors", label: `${vendorLabel}s`, className: "coverage-menu-subcontractors"},
    {key: "certificates", label: "Certificates", className: "coverage-menu-certificates"}
  ]
  
  async function getProjectCertificates() {
    const result = await dispatch(clientActions.getProjectCertificates(projectId))
    setCertificates(result?.payload?.certificates)
  }
  useEffect(()=>{
    if(!location.hash) {
      navigate(location.pathname + `#/${subMenuItems[0].key}`)
    }
    getProjectCertificates()
    if(selectedClient?.id && !vendors) {
      dispatch(vendorActions.fetchVendors(selectedClient.clientID))
    }
  }, [])

  const activeItem = String(location.hash).replace("#/", "")
  const dateStart = awsDateToUS(project?.startDate)
  const dateEnd = awsDateToUS(project?.endDate)
  // const [updateAllSubsModalOpen, setUpdateAllSubsModalOpen] = useState(false);
  const [projectData, setProjectData] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState();
  const [editCoverages, setEditCoverage] = useState(false)
  const [refreshCoverageView, setRefreshCoverageView] = useState(0)
  const [coverageLoading, setCoverageLoading] = useState(false)
  const [addSubsModalOpen, setAddSubsModalOpen] = useState(false)
  const [addSubcontractorsLoading, setAddSubcontractorsLoading] = useState(false)

  // Tour state
  const {
    isRunning: isTourRunning,
    stepIndex: tourStepIndex,
    completedSteps,
    startTour,
    stopTour,
    goToStep,
  } = useEntityTour(COVERAGES_TOUR.id, projectId);

  const [tourKey, setTourKey] = useState(0);

  // Navigate to the appropriate tab when tour step changes
  const navigateToTab = useCallback((tabKey) => {
    if (tabKey && location.pathname) {
      navigate(location.pathname + `#/${tabKey}`);
    }
  }, [navigate, location.pathname]);

  // Custom Joyride callback handler with tab navigation
  const handleTourCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle step completion and navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        const nextStep = COVERAGES_TOUR.steps[index + 1];
        if (nextStep?.data?.tab) {
          navigateToTab(nextStep.data.tab);
          setTimeout(() => {
            goToStep(index + 1);
          }, 300);
        } else {
          goToStep(index + 1);
        }
      } else if (action === ACTIONS.PREV) {
        const prevStep = COVERAGES_TOUR.steps[index - 1];
        if (prevStep?.data?.tab) {
          navigateToTab(prevStep.data.tab);
          setTimeout(() => {
            goToStep(index - 1);
          }, 300);
        } else {
          goToStep(index - 1);
        }
      }
    }

    // Handle tour completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour();
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      stopTour();
    }
  }, [navigateToTab, goToStep, stopTour]);

  // Start the tour
  const handleStartTour = useCallback(() => {
    navigateToTab('coverages');
    setTourKey(prev => prev + 1);
    setTimeout(() => {
      startTour();
    }, 300);
  }, [navigateToTab, startTour]);

  const navToVendor = (vendorId) => {
    const destination = getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, {
      clientId: selectedClient.organizationID,
      vendorId,
      referrer: NAVIGATED_FROM.PROJECT,
      referrerId: projectId
    })
  
    navigate(destination)
  }

  function backToProjects() {
    const destination = getNavigationPath(NAV_PATH.CLIENT_PROJECTS, {clientId})
    navigate(destination)
  }

  async function onSaveProject() {
    setSaving(true)
    const result = await dispatch(clientActions.updateProject({projectData, clientID: selectedClient.clientID}))
    handleUpdateResult(result)
    setSaving(false)
  }

  function onProjectDataUpdate(name, value) {
    setProjectData({
      ...projectData,
      [name]: value
    })
  }

  async function onUpdateProjectStatus(status) {
    setSaving(true)
    const result = await dispatch(clientActions.updateProjectStatus({
      clientID: selectedClient.clientID,
      id: project.id,
      status,
    }))
    handleUpdateResult(result)
    setSaving(false)
  }

  function handleUpdateResult(result) {
    if(result?.payload?.success) {
      setProjectData(false)
      setMessage({
        severity: "success",
        text: `Successfully updated ${projectLabel}`
      })
    } else {
      setMessage({
        severity: "error",
        text: `Unable to updated ${projectLabel}`
      })
    }
  }

  function navToCertificate(id) {
    const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
      clientId: selectedClient.id,
      vendorId: NAVIGATED_FROM.PROJECT,
      certificateId: id
    })
    navigate(certificatePath, {
      state: {
        referrer: location.pathname,
      }
    })
  }

  function handleCoverageCancel() {
    setEditCoverage(false)
  }

  async function handleCoverageSave(data, requiredDescriptionOfOperation) {
    setCoverageLoading(true)
    const result = await dispatch(clientActions.updateProject({
      projectData: {
        id: project.id,
        requiredCoverages: data,
        requiredDescriptionOfOperation
      },
      clientID: selectedClient?.clientID
    }))
    if(result?.payload?.success) {
      setEditCoverage(false)
      setRefreshCoverageView(refresh => refresh+1)
      setMessage({
        severity: "success",
        text: "Successfully updated coverage"
      })
    } else {
      setMessage({
        severity: "error",
        text: "Unable to update coverage"
      })
    }

    setCoverageLoading(false)
  }

  async function handleSaveSubcontractors(selected) {
    setAddSubcontractorsLoading(true)
    const result = await dispatch(clientActions.updateProjectVendors({
      projectID: project.id,
      selectedVendors: selected,
      existingProjectVendors: project?.vendors?.items
    }))
    setAddSubcontractorsLoading(false)
    if(result?.payload?.success) {
      setAddSubsModalOpen(false)
      setMessage({
        severity: "success",
        text: `Successfully updated subcontractors for ${projectLabel}`
      })
    } else {
      setMessage({
        severity: "error",
        text: `Unable to update subcontractors for ${projectLabel}`
      })
    }
  }

  return (
    <div className="project-details-screen">
      <CoverageUpdateModal
        title={`Update ${projectLabel} Coverage Requirements`}
        open={editCoverages}
        loading={coverageLoading}
        onClose={handleCoverageCancel}
        coverages={coverages}
        clientCoverages={selectedClient?.clientCoverages?.items}
        onSubmit={handleCoverageSave}
        clientMode={selectedClient?.mode}
        showHelperText={true}
        descriptionOfOperationValidation={project?.requiredDescriptionOfOperation}
      />
      <Snackbar message={message} setMessage={setMessage} />

      {/* Coverages Tour */}
      <Joyride
        key={tourKey}
        steps={COVERAGES_TOUR.steps}
        run={isTourRunning}
        stepIndex={tourStepIndex}
        continuous
        scrollToFirstStep
        disableOverlayClose
        disableScrollParentFix
        spotlightClicks
        callback={handleTourCallback}
        styles={DEFAULT_TOUR_STYLES}
        tooltipComponent={CustomTooltip}
        floaterProps={{
          disableAnimation: true,
        }}
      />

      {/* Tour Outline Sidebar */}
      {isTourRunning && (
        <TourOutline
          steps={COVERAGES_TOUR.steps}
          currentStep={tourStepIndex}
          completedSteps={completedSteps}
          onStepClick={(index) => {
            const targetStep = COVERAGES_TOUR.steps[index];
            const targetTab = targetStep?.data?.tab;
            const currentTab = activeItem;
            const needsTabChange = targetTab && targetTab !== currentTab;

            requestAnimationFrame(() => {
              if (needsTabChange) {
                navigateToTab(targetTab);
              }

              const delay = needsTabChange ? 150 : 50;
              setTimeout(() => {
                goToStep(index);
              }, delay);
            });
          }}
          title={TOUR_TITLES[COVERAGES_TOUR.id]}
        />
      )}

      <ProjectInfoUpdateModal
        open={projectData !== false}
        projectData={projectData}
        onClose={()=>setProjectData(false)}
        onSave={onSaveProject}
        onUpdate={onProjectDataUpdate}
        loading={saving}
        projectLabel={projectLabel}
      />
      {/*<UpdateAllSubsCoverageModal*/}
      {/*  open={updateAllSubsModalOpen}*/}
      {/*  onClose={()=>setUpdateAllSubsModalOpen(false)}*/}
      {/*/>*/}
      <Button onClick={backToProjects}>
        <KeyboardArrowLeftIcon />
        {`Back to ${projectLabel}s`}
      </Button>
      <Box sx={{mt: 2, mb: 4}} className="coverage-project-header">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" component="span">
            {project?.name}
          </Typography>
          <Tooltip title="Take a guided tour">
            <IconButton
              onClick={handleStartTour}
              size="small"
              sx={{ ml: 0.5 }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <StatusIndicatorSelect
            statuses={projectStatuses}
            currentStatus={project?.status}
            onUpdateStatus={onUpdateProjectStatus}
            disableSelection={!hasEditPrivileges}
          />
          <Button
            startIcon={<EditIcon fontSize="small" />}
            onClick={()=>setProjectData({
              ...project,
              startDate: awsDateToUS(project?.startDate),
              endDate: awsDateToUS(project?.endDate),
            })}
            color={"primary"}
            style={{ marginLeft: "auto" }}
            disabled={!hasEditPrivileges}
          >
            {`UPDATE ${projectLabel}`.toUpperCase()}
          </Button>
        </Stack>
        <Typography variant="subtitle2" color="text.secondary" component="span">
          {
            `Start Date: ${(dateStart && dateStart !== 'Invalid Date') ? dateStart : "-" } | `
          }
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" component="span">
          {
            `End Date: ${(dateEnd && dateEnd !== 'Invalid Date') ? dateEnd : "-" } | `
          }
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" component="span">
          {project?.description}
        </Typography>
        {project?.address && 
        <Typography variant="subtitle2" color="text.secondary" component="span">
          {` | ${project?.address}`}
        </Typography>
        }
        {project?.state && 
        <Typography variant="subtitle2" color="text.secondary" component="span">
          {` | ${project?.state}`}
        </Typography>
        }
        {project?.zip && 
        <Typography variant="subtitle2" color="text.secondary" component="span">
          {` | ${project?.zip}`}
        </Typography>
        }
        <Divider />
      </Box>

      <SideMenuContainer items={subMenuItems}>
        {
          (activeItem === subMenuItems[0].key) &&
          <BoxShadow>
            <SectionTitle sx={{mb: 2}}>
              {subMenuItems[0].label}
              <Button
                className="coverage-update-button"
                onClick={() => setEditCoverage(true)}
                disabled={!hasEditPrivileges}
              >
                Update
              </Button>
            </SectionTitle>

            <CoverageForm descriptionOfOperationValidation={project?.requiredDescriptionOfOperation} isEdit={false} coverages={coverages} refresh={refreshCoverageView}/>
            {
              !coverages?.length &&
              <Typography variant="subtitle2" sx={{mt:3}} color="text.secondary">
                No requirement set for this {projectLabel}.
              </Typography>
            }
          </BoxShadow>
        }
        {
          (activeItem === subMenuItems[1].key) &&
          <BoxShadow>
            <AddSubcontractorsModal
              open={addSubsModalOpen}
              onClose={()=>setAddSubsModalOpen(false)}
              vendors={vendors}
              projectVendors={project?.vendors?.items}
              onSave={handleSaveSubcontractors}
              loading={addSubcontractorsLoading}
              vendorLabel={vendorLabel}
              projectLabel={projectLabel}
            />
            <SectionTitle sx={{mb: 2}}>
              {subMenuItems[1].label}
              <Button disabled={!hasEditPrivileges} sx={{float: "right"}} onClick={()=>setAddSubsModalOpen(true)}>
                {`+ ADD ${vendorLabel}S TO ${projectLabel}`.toUpperCase()}
              </Button>
            </SectionTitle>
            <VendorsTable
              columns={["name", "status", "city", "state", "createdAt", "email"]}
              vendors={project?.vendors?.items?.filter(vendor => vendor.clientVendor && (filterByAssignedVendors ? filterByAssignedVendors.includes(vendor.clientVendor.id) : true)).map(vendor => vendor.clientVendor)}
              clientMode={client_mode.project}
              navToVendor={navToVendor}
              compact={false}
            />
          </BoxShadow>
        }
        {
          (activeItem === subMenuItems[2].key) &&
          <BoxShadow>
            <SectionTitle sx={{mb: 2}}>{subMenuItems[2].label}</SectionTitle>
            <CertificateTable
              onView={navToCertificate}
              certificates={certificates || []}
              clientMode={client_mode.project}
              compact={true}
            />
          </BoxShadow>
        }
      </SideMenuContainer>
    </div>
  )
}
